import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ChevronDown, Globe2, Plane, Languages, HeartHandshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/medical/Navbar";
import { Footer } from "../components/medical/Footer";
import { WhatsappFloat } from "../components/medical/WhatsappFloat";
import { Breadcrumbs } from "../components/medical/Breadcrumbs";
import { SEO } from "../components/medical/SEO";
import { ConsultationForm } from "../components/medical/ConsultationForm";
import { getSpecialty } from "../data/specialties";
import { getTreatmentsBySpecialty } from "../data/treatments";
import { getDoctorsBySpecialty } from "../data/doctors";

export const SpecialtyPage = () => {
    const { slug } = useParams();
    const specialty = getSpecialty(slug);
    const [openFaq, setOpenFaq] = useState(0);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    if (!specialty) return <Navigate to="/" replace />;

    const treatments = getTreatmentsBySpecialty(slug);
    const doctors = getDoctorsBySpecialty(slug);
    const Icon = specialty.icon;

    const schema = {
        "@context": "https://schema.org",
        "@type": "MedicalSpecialty",
        name: specialty.name,
        description: specialty.overview,
    };

    return (
        <div className="min-h-screen bg-background font-body">
            <SEO
                title={specialty.name}
                description={specialty.overview.slice(0, 160)}
                image={specialty.image}
                schema={schema}
            />
            <Navbar />
            <main className="pt-32 md:pt-40">
                {/* Hero */}
                <section className="relative py-12 md:py-20 bg-gradient-to-br from-brand-50/60 via-white to-orange-50/40 border-b border-stone-200">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <Breadcrumbs
                            items={[
                                { label: "Specialities", to: "/#specialities" },
                                { label: specialty.shortName },
                            ]}
                        />
                        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-7">
                                <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-brand-700 text-white shadow-md">
                                    <Icon className="h-7 w-7" />
                                </div>
                                <h1
                                    data-testid="specialty-name"
                                    className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.05]"
                                >
                                    {specialty.name}
                                </h1>
                                <p className="mt-4 text-lg text-brand-700 font-semibold max-w-xl">
                                    {specialty.tagline}
                                </p>
                                <p className="mt-5 text-stone-600 leading-relaxed max-w-2xl">
                                    {specialty.overview}
                                </p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Button
                                        asChild
                                        className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 h-12"
                                        data-testid="specialty-cta"
                                    >
                                        <a href="#book">Get Free Doctor's Opinion</a>
                                    </Button>
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="rounded-full border-stone-300 hover:bg-stone-50 px-7 h-12"
                                    >
                                        <a href="#treatments">View Treatments</a>
                                    </Button>
                                </div>
                            </div>
                            <div className="lg:col-span-5">
                                <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                                    <img
                                        src={specialty.image}
                                        alt={specialty.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Conditions + Categories */}
                <section className="py-16 md:py-20">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
                        {specialty.conditions.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                                    Common conditions treated
                                </div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    Conditions we manage
                                </h2>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {specialty.conditions.map((c) => (
                                        <span
                                            key={c}
                                            className="inline-flex px-3 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-800"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {specialty.categories.length > 0 && (
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                                    Treatment categories
                                </div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    Care across sub-specialities
                                </h2>
                                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {specialty.categories.map((c) => (
                                        <div
                                            key={c}
                                            className="p-4 rounded-2xl bg-brand-50/50 border border-brand-100 text-sm font-semibold text-stone-800"
                                        >
                                            {c}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Treatments */}
                {treatments.length > 0 && (
                    <section id="treatments" className="py-16 md:py-20 bg-stone-50/60 border-y border-stone-200">
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <div className="max-w-2xl mb-10">
                                <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                                    Treatments & Surgeries
                                </div>
                                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
                                    Procedures we coordinate
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                {treatments.map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={`/treatments/${t.slug}`}
                                        data-testid={`specialty-treatment-${t.slug}`}
                                        className="group rounded-3xl bg-white border border-stone-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
                                    >
                                        <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                                            <img
                                                src={t.image}
                                                alt={t.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-5 flex-1 flex flex-col">
                                            <div className="font-display text-lg font-bold text-stone-900 leading-tight">
                                                {t.shortName}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-1 flex-1">
                                                {t.tagline}
                                            </div>
                                            <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-brand-700">
                                                Learn more →
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Associated Doctors */}
                {doctors.length > 0 && (
                    <section className="py-16 md:py-20">
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <div className="max-w-2xl mb-10">
                                <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                                    Associated doctors
                                </div>
                                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
                                    Empanelled specialists
                                </h2>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {doctors.map((d) => (
                                    <Link
                                        key={d.slug}
                                        to={`/doctors/${d.slug}`}
                                        data-testid={`specialty-doctor-${d.slug}`}
                                        className="group rounded-3xl bg-white border border-stone-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                                    >
                                        <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                                            <img
                                                src={d.image}
                                                alt={d.name}
                                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-5">
                                            <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold">
                                                {d.specialtyLabel}
                                            </div>
                                            <div className="font-display text-lg font-bold text-stone-900 mt-1">
                                                {d.name}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-1">
                                                {d.experience}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* International Patient Assistance */}
                <section className="py-16 md:py-20 bg-brand-700 text-white">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="text-xs uppercase tracking-[0.2em] text-orange-300 font-semibold mb-3">
                                International Patient Assistance
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                                A single coordinator handles your entire journey.
                            </h2>
                            <div className="mt-8 grid sm:grid-cols-2 gap-5 max-w-2xl">
                                {[
                                    { icon: Plane, t: "Medical visa & travel" },
                                    { icon: Languages, t: "Translators & interpreters" },
                                    { icon: Globe2, t: "Hotel & guest house booking" },
                                    { icon: HeartHandshake, t: "Hospital admission & billing" },
                                ].map((it) => (
                                    <div
                                        key={it.t}
                                        className="flex items-center gap-3 p-4 rounded-2xl bg-white/10 border border-white/10"
                                    >
                                        <it.icon className="h-5 w-5 text-orange-300" />
                                        <span className="text-sm font-medium">
                                            {it.t}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div id="book">
                                <ConsultationForm
                                    context={`Specialty: ${specialty.name}`}
                                    treatmentSuggestion={specialty.shortName}
                                    title={`Enquire about ${specialty.shortName}`}
                                    subtitle="Free doctor's opinion within 24 hours."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                {specialty.faqs.length > 0 && (
                    <section className="py-16 md:py-20">
                        <div className="mx-auto max-w-3xl px-5 sm:px-8">
                            <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3 text-center">
                                Frequently Asked Questions
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 text-center">
                                Common questions answered
                            </h2>
                            <div className="mt-10 space-y-3">
                                {specialty.faqs.map((f, i) => (
                                    <div
                                        key={i}
                                        className="rounded-2xl border border-stone-200 bg-white overflow-hidden"
                                    >
                                        <button
                                            onClick={() =>
                                                setOpenFaq(openFaq === i ? -1 : i)
                                            }
                                            data-testid={`faq-toggle-${i}`}
                                            className="w-full flex items-center justify-between gap-4 p-5 text-left"
                                        >
                                            <span className="font-display font-semibold text-stone-900">
                                                {f.q}
                                            </span>
                                            <ChevronDown
                                                className={`h-4 w-4 flex-shrink-0 text-stone-500 transition-transform ${
                                                    openFaq === i ? "rotate-180" : ""
                                                }`}
                                            />
                                        </button>
                                        {openFaq === i && (
                                            <div className="px-5 pb-5 text-sm text-stone-600 leading-relaxed">
                                                {f.a}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
            <Footer />
            <WhatsappFloat />
        </div>
    );
};
